import './index.less';

import { Button, Dropdown, Input, message, Select, Skeleton, Space, Tabs } from 'antd';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { useLifecycles } from 'react-use';

import { SaveOutlined } from '@ant-design/icons';
import { PageHeader } from '@ant-design/pro-components';

import UpdateDunningInfoModal from '../../../components/UpdateDunningInfoModal';
import {
  AccountEnquiryResponse,
  collectionStateReset,
} from '../../../features/collection/collectionSlice';
import { smeApplicationDetailsStateReset } from '../../../features/smeApplication/smeApplicationSlice';
import { searchDisbursedAgreementThunk } from '../../../services/agreementService/agreementThunk';
import {
  getAccountDetailsThunk,
  getAccountTenureDetailsThunk,
  getDueAccountTenureDetailsThunk,
  getEnglishAccountStatementbyAgreementIdThunk,
  getMalayAccountStatementbyAgreementIdThunk,
} from '../../../services/collectionService/collectionThunk';
import { getSmeApplicationDetailsThunk } from '../../../services/smeApplicationService/smeApplicationThunk';
import { getSmeAgreementDetailsThunk } from '../../../services/smeAgreementService/smeAgreementThunk';
import { AppDispatch, RootState } from '../../../store/store';
import { saveBlobAsPDF } from '../../../utils/file';
import AccountDetailsCard from '../AccountDetails';
import CustomerDetailsCard from '../CustomerDetails';
import DunningHistory from '../DunningHistory';
import EmergencyInformation from '../EmergencyInformation';
import EmploymentInformation from '../EmploymentInformation';
import InsertPaymentModal from '../InsertPaymentModal';
import LegalHistory from '../LegalHistory';
import LoanInformationCard from '../LoanInformationCard';
import NoticeHistory from '../NoticeHistory';
import PaymentHistory from '../PaymentHistory';
import SearchAgreementResult from '../SearchAgreementResult';
import SmsHistory from '../SmsHistory';
import SpecialTaggingHistory from '../SpecialTaggingHistory';
import TransactionHistoryCard from '../TransactionHistory';
import jwt_decode from 'jwt-decode';

import type { MenuProps } from 'antd';
import CollectionActivity from '../CollectionActivity';
import { ActionType } from '../../../enum/actionType';
import EmailHistory from '../EmailHistory';
import DirectDebit from '../DirectDebitCard';
import CollectionHistory from '../CollectionHistoryLog';
import CollectionSystemLog from '../CollectionSystemLog';
import { Role } from '../../../enum/roles';
import SelfBilledEInvoiceSubmissionHistory from '../SelfBilledEInvoiceSubmissionHistory';
import SmeCustomerDetails from '../SmeCustomerDetails';
import { SmeCompanyDetails } from '../SmeCompanyDetails';
import { SME_ORGANIZATION_TYPE } from '../../../enum/smeApplication';
import { SmeDisbursementDetails } from '../SmeDisbursementDetails';
import { SmeDirectorDetails } from '../SmeDirectorDetails';
import { isSmeOrganization } from '../../../utils/string.util';

const { Search } = Input;

export type AccountEnquiryContentProps = {
  searchAgreementNo?: string;
  modalView?: boolean;
  viewAccountDetailsOnly?: boolean;
  callback?: () => void;
};

const AccountEnquiryContent: React.FC<AccountEnquiryContentProps> = (
  props: AccountEnquiryContentProps
) => {
  const { searchAgreementNo, viewAccountDetailsOnly, callback } = props;

  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);

  const { currentUser } = useSelector((state: RootState) => state.user);

  const [accountEnquiry, setAccountEnquiry] = useState<AccountEnquiryResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [resultNotFound, setResultNotFound] = useState(false);
  const [searchType, setSearchType] = useState('agreementNo');
  const [searchResultVisible, setSearchResultVisible] = useState(false);
  const [dunningModalVisible, setDunningModalVisible] = useState(false);
  const [searchResultData, setSearchResultData] = useState([]);
  const token = localStorage.getItem('accessToken') ?? '';
  const decode: any = token ? jwt_decode(token) : null;
  const currentRole = decode?.role;

  const onSearch = async (value: string) => {
    const trimmedValue = value.trim();

    if (trimmedValue) {
      setLoading(true);
      await dispatch(searchDisbursedAgreementThunk({ [searchType]: trimmedValue }))
        .unwrap()
        .then((res) => {
          setSearchResultData(res);
          if (res && res.length > 0) {
            setSearchResultVisible(true);
          } else {
            message.error('Result Not Found');
            setResultNotFound(true);
          }
        })
        .catch((error) => {
          setResultNotFound(true);
          console.error('[onSearch] Error during search:', error);
        })
        .finally(() => {
          setLoading(false);
        });
    } else {
      dispatch(collectionStateReset());
    }
  };

  const infoTabItems = [
    {
      label: 'Loan Info',
      key: '1',
      children: accountEnquiry && <LoanInformationCard accountEnquiry={accountEnquiry} />,
    },
  ]
    .concat(
      accountEnquiry?.accountDetails?.organizationType === SME_ORGANIZATION_TYPE.COMPANY
        ? []
        : [
            {
              label: 'Customer Info',
              key: '2',
              children: accountEnquiry && (
                <div className="bg-white rounded-lg">
                  {isSmeOrganization(accountEnquiry?.accountDetails?.organizationType) ? (
                    <SmeCustomerDetails />
                  ) : (
                    <>
                      <CustomerDetailsCard accountEnquiry={accountEnquiry} />
                      <EmploymentInformation accountEnquiry={accountEnquiry} />
                      <EmergencyInformation accountEnquiry={accountEnquiry} />
                      <DirectDebit searchAgreementNo={searchAgreementNo ?? ''} />
                    </>
                  )}
                </div>
              ),
            },
          ]
    )
    .concat(
      accountEnquiry?.accountDetails?.organizationType === SME_ORGANIZATION_TYPE.COMPANY ||
        accountEnquiry?.accountDetails?.organizationType === SME_ORGANIZATION_TYPE.PARTNERSHIP
        ? [
            {
              label: 'Company Info',
              key: '3',
              children: <SmeCompanyDetails />,
            },
          ]
        : []
    )
    .concat(
      accountEnquiry?.accountDetails?.organizationType === SME_ORGANIZATION_TYPE.COMPANY ||
        accountEnquiry?.accountDetails?.organizationType === SME_ORGANIZATION_TYPE.PARTNERSHIP
        ? [
            {
              label: 'Director Info',
              key: '4',
              children: <SmeDirectorDetails />,
            },
          ]
        : []
    )
    .concat(
      isSmeOrganization(accountEnquiry?.accountDetails?.organizationType)
        ? [
            {
              label: 'Disbursement Info',
              key: '5',
              children: <SmeDisbursementDetails />,
            },
          ]
        : []
    );

  const historyTabItems = [
    {
      label: 'Payment History',
      key: 'payment',
      children: accountEnquiry && <PaymentHistory accountEnquiry={accountEnquiry} />,
    },
    {
      label: 'Transaction History',
      key: 'transaction',
      children: accountEnquiry && <TransactionHistoryCard accountEnquiry={accountEnquiry} />,
    },
    {
      label: 'Dunning History',
      key: 'dunning',
      children: accountEnquiry && <DunningHistory accountEnquiry={accountEnquiry} />,
    },
    {
      label: 'SMS History',
      key: 'sms',
      children: accountEnquiry && <SmsHistory accountEnquiry={accountEnquiry} />,
    },
    {
      label: 'Email History',
      key: 'email',
      children: accountEnquiry && <EmailHistory accountEnquiry={accountEnquiry} />,
    },
    {
      label: 'Notice History',
      key: 'notice',
      children: accountEnquiry && <NoticeHistory accountEnquiry={accountEnquiry} />,
    },
    {
      label: 'Legal History',
      key: 'legal',
      children: accountEnquiry && <LegalHistory accountEnquiry={accountEnquiry} />,
    },
    {
      label: 'Special Tagging History',
      key: 'specialTagging',
      children: accountEnquiry && <SpecialTaggingHistory accountEnquiry={accountEnquiry} />,
    },
    {
      label: 'Field Visit History',
      key: 'fieldVisitHistory',
      children: accountEnquiry && <CollectionActivity accountEnquiry={accountEnquiry} />,
    },
    {
      label: 'Collection History Log',
      key: 'collectionHistoryLog',
      children: accountEnquiry && <CollectionHistory accountEnquiry={accountEnquiry} />,
    },
    {
      label: 'E-Invoice',
      key: 'eInvoice',
      children: accountEnquiry && (
        <SelfBilledEInvoiceSubmissionHistory accountEnquiry={accountEnquiry} />
      ),
    },
  ];

  // This tab is for redCash super admin only
  const redCashSuperAdminHistoryTabItems = [
    {
      label: 'Payment History',
      key: 'payment',
      children: <PaymentHistory accountEnquiry={accountEnquiry ?? {}} />,
    },
    {
      label: 'Transaction History',
      key: 'transaction',
      children: accountEnquiry && <TransactionHistoryCard accountEnquiry={accountEnquiry} />,
    },
    {
      label: 'Dunning History',
      key: 'dunning',
      children: accountEnquiry && <DunningHistory accountEnquiry={accountEnquiry} />,
    },
    {
      label: 'SMS History',
      key: 'sms',
      children: accountEnquiry && <SmsHistory accountEnquiry={accountEnquiry} />,
    },
    {
      label: 'Email History',
      key: 'email',
      children: accountEnquiry && <EmailHistory accountEnquiry={accountEnquiry} />,
    },
    {
      label: 'Notice History',
      key: 'notice',
      children: accountEnquiry && <NoticeHistory accountEnquiry={accountEnquiry} />,
    },
    {
      label: 'Legal History',
      key: 'legal',
      children: accountEnquiry && <LegalHistory accountEnquiry={accountEnquiry} />,
    },
    {
      label: 'Special Tagging History',
      key: 'specialTagging',
      children: accountEnquiry && <SpecialTaggingHistory accountEnquiry={accountEnquiry} />,
    },
    {
      label: 'Field Visit History',
      key: 'fieldVisitHistory',
      children: accountEnquiry && <CollectionActivity accountEnquiry={accountEnquiry} />,
    },
    {
      label: 'Collection History Log',
      key: 'collectionHistoryLog',
      children: accountEnquiry && <CollectionHistory accountEnquiry={accountEnquiry} />,
    },
    {
      label: 'Collection System Log',
      key: 'collectionSystemLog',
      children: accountEnquiry && <CollectionSystemLog accountEnquiry={accountEnquiry} />,
    },
    {
      label: 'E-Invoice',
      key: 'eInvoice',
      children: accountEnquiry && (
        <SelfBilledEInvoiceSubmissionHistory accountEnquiry={accountEnquiry} />
      ),
    },
  ];

  const handleDownloadAccountStatementClick: MenuProps['onClick'] = (e) => {
    setDownloading(true);
    if (e.key === 'en') {
      dispatch(
        getEnglishAccountStatementbyAgreementIdThunk({
          agreementId: accountEnquiry?.agreementDetails?.id,
        })
      )
        .unwrap()
        .then((response) => {
          const filename = `Statement_of_Account_${accountEnquiry?.agreementDetails?.agreementNo}_EN.pdf`;
          saveBlobAsPDF(response, filename);
        })
        .finally(() => setDownloading(false));
    } else {
      if (e.key === 'bm') {
        dispatch(
          getMalayAccountStatementbyAgreementIdThunk({
            agreementId: accountEnquiry?.agreementDetails?.id,
          })
        )
          .unwrap()
          .then((response) => {
            const filename = `Statement_of_Account_${accountEnquiry?.agreementDetails?.agreementNo}_BM.pdf`;
            saveBlobAsPDF(response, filename);
          })
          .finally(() => setDownloading(false));
      }
    }
  };

  const fetchAccountEnquiry = async (agreementNo: string) => {
    setLoading(true);
    await dispatch(getAccountDetailsThunk({ agreementNo }))
      .unwrap()
      .then(async (res) => {
        await dispatch(
          getDueAccountTenureDetailsThunk({
            agreementNo: res?.accountDetails?.agreementNo,
          })
        )
          .unwrap()
          .then((tenureDetailsRes) => {
            res = { ...res, tenureDetails: tenureDetailsRes.tenureDetails };
          })
          .catch((err) => {
            console.error('Error fetching due account tenure details:', err);
            message.error('Failed to fetch due account tenure details.');
          });

        await dispatch(
          getAccountTenureDetailsThunk({
            agreementNo: res?.accountDetails?.agreementNo,
          })
        )
          .unwrap()
          .then((totalTenureRes) => {
            res = { ...res, totalTenure: totalTenureRes.tenureDetails };
          })
          .catch((err) => {
            console.error('Error fetching total account tenure details:', err);
            message.error('Failed to fetch total account tenure details.');
          });

        if (
          res?.accountDetails?.agreementId &&
          (res?.accountDetails?.organizationType === SME_ORGANIZATION_TYPE.COMPANY ||
            res?.accountDetails?.organizationType === SME_ORGANIZATION_TYPE.PARTNERSHIP)
        ) {
          await dispatch(
            getSmeAgreementDetailsThunk({ agreementId: res.accountDetails.agreementId })
          )
            .unwrap()
            .then((smeAgreement) =>
              dispatch(
                getSmeApplicationDetailsThunk({
                  applicationId: smeAgreement.smeApplicationId,
                })
              ).unwrap()
            )
            .catch((err) => {
              console.error('Error fetching SME application details:', err);
              dispatch(smeApplicationDetailsStateReset());
            });
        }

        setAccountEnquiry(res);

        if (!viewAccountDetailsOnly) {
          queryParams.set('searchAgreementNo', agreementNo);
          const newSearch = queryParams.toString();
          navigate({
            pathname: location.pathname,
            search: newSearch,
          });
        }
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleSearchResultSelect = async (agreementNo: string) => {
    setSearchResultVisible(false);
    await fetchAccountEnquiry(agreementNo);
  };

  const handleSearchResultCancel = () => {
    setSearchResultVisible(false);
  };

  useLifecycles(async () => {
    if (searchAgreementNo) {
      setSearchType('agreementNo');
      await fetchAccountEnquiry(searchAgreementNo);
    }
  });

  return (
    <>
      <PageHeader className="px-0 mb-2" title="Account Enquiry">
        {!viewAccountDetailsOnly && 'Search for disbursed agreement account details'}
      </PageHeader>

      {!viewAccountDetailsOnly && (
        <>
          <div className="mb-4">
            <div className="flex gap-1 mb-1">
              <Input.Group compact>
                <Select
                  className="w-[10rem]"
                  value={searchType}
                  onChange={(value) => {
                    setResultNotFound(false);
                    setSearchType(value);
                  }}
                  options={[
                    { value: 'agreementNo', label: 'Agreement No' },
                    { value: 'applicantNric', label: 'Applicant NRIC' },
                    { value: 'applicantFirstName', label: 'Applicant Name' },
                    { value: 'applicantPhoneNo', label: 'Phone No' },
                    { value: 'applicantEmail', label: 'Email' },
                    { value: 'staffId', label: 'Staff ID' },
                    { value: 'applicantPartnerId', label: 'Partner ID' },
                  ]}
                />
                <Search
                  className="w-[20rem]"
                  placeholder="Input search text"
                  enterButton="Search"
                  loading={loading}
                  onSearch={onSearch}
                  defaultValue={searchAgreementNo}
                  status={resultNotFound ? 'error' : ''}
                  onChange={() => setResultNotFound(false)}
                />
              </Input.Group>
              {accountEnquiry?.accountDetails && (
                <>
                  {currentRole !== Role.FINANCE && currentRole !== Role.CUSTOMER_SERVICE && (
                    <InsertPaymentModal />
                  )}

                  <Button onClick={() => setDunningModalVisible(true)}>
                    <Space>Update Dunning History</Space>
                  </Button>

                  <Dropdown
                    menu={{
                      items: [
                        { label: 'English', key: 'en' },
                        { label: 'Bahasa Malaysia', key: 'bm' },
                      ],
                      onClick: handleDownloadAccountStatementClick,
                    }}
                  >
                    <Button loading={downloading}>
                      <Space>
                        Download Account Statement
                        <SaveOutlined />
                      </Space>
                    </Button>
                  </Dropdown>
                </>
              )}
            </div>
          </div>

          {searchResultVisible && (
            <SearchAgreementResult
              visible={searchResultVisible}
              data={searchResultData}
              onOk={handleSearchResultSelect}
              onCancel={handleSearchResultCancel}
            />
          )}
        </>
      )}

      {loading ? (
        <Skeleton />
      ) : (
        <>
          {accountEnquiry?.accountDetails && (
            <div>
              <AccountDetailsCard accountEnquiry={accountEnquiry} />
              <br />
              <Tabs items={infoTabItems} />
              <br />
              <Tabs
                items={
                  currentUser.data?.username === 'REDCASH_SUPER_ADMIN'
                    ? redCashSuperAdminHistoryTabItems
                    : historyTabItems
                }
              />
            </div>
          )}
        </>
      )}

      {dunningModalVisible && (
        <UpdateDunningInfoModal
          visible={dunningModalVisible}
          hideButton={true}
          onCancelCallback={() => setDunningModalVisible(false)}
          accountDetailsId={accountEnquiry?.accountDetails?.id}
          agreementNo={accountEnquiry?.accountDetails!.agreementNo}
          statusActivity={ActionType.BP}
        />
      )}
    </>
  );
};

AccountEnquiryContent.defaultProps = {
  modalView: false,
  viewAccountDetailsOnly: false,
};

export default AccountEnquiryContent;
